import { useMemo } from 'react';

import { Event } from '@/feature/event-details-sbk/types';
import { getTeamColors } from '@/feature/event-details-sbk/utils/get-team-colors';
import { TeamOrder, getTeamDisplayName } from '@/utils/sport-utils';

export type MatchupTeamRow = {
    side: 'home' | 'away';
    teamName: string;
    logoUrl: string;
    primaryColor: string;
    secondaryColor: string;
    shouldShowFallbackLogo: boolean;
    score?: number;
};

// Shared team-row derivation used by every matchup card variant. Returns rows in display order
// (per the sport's teamOrder). Static/Tennis callers simply ignore the fields they don't need.
export const useMatchupTeamRows = (event: Event, teamOrder: TeamOrder): MatchupTeamRow[] =>
    useMemo<MatchupTeamRow[]>(() => {
        const { home: homeColors, away: awayColors } = getTeamColors(event);
        const sportName = event.sport.name;
        const away: MatchupTeamRow = {
            side: 'away',
            teamName: getTeamDisplayName(event.away_team, sportName),
            logoUrl: event.away_team.logo_url ?? '',
            primaryColor: awayColors.primary,
            secondaryColor: awayColors.secondary,
            shouldShowFallbackLogo: awayColors.shouldShowFallbackLogo,
            score: event.event_details?.away_score,
        };
        const home: MatchupTeamRow = {
            side: 'home',
            teamName: getTeamDisplayName(event.home_team, sportName),
            logoUrl: event.home_team.logo_url ?? '',
            primaryColor: homeColors.primary,
            secondaryColor: homeColors.secondary,
            shouldShowFallbackLogo: homeColors.shouldShowFallbackLogo,
            score: event.event_details?.home_score,
        };
        return teamOrder[0] === 'home' ? [home, away] : [away, home];
    }, [event, teamOrder]);
